import { Analyse, createEmptyAnalyse } from "./schema";

/* Reaktiver Store mit sessionStorage-Persistenz (§41).
   Keine hochsensiblen Dokument-Inhalte werden gespeichert – nur Metadaten. */

const STORAGE_KEY = "mwa_demo_state_v1";

type Listener = () => void;

export class Store {
  state: Analyse;
  private listeners = new Set<Listener>();
  private saveTimer: number | null = null;

  constructor() {
    this.state = this.load() ?? createEmptyAnalyse();
  }

  private load(): Analyse | null {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as Partial<Analyse>;
      // Defensive Zusammenführung mit dem aktuellen Schema
      return { ...createEmptyAnalyse(), ...parsed } as Analyse;
    } catch {
      return null;
    }
  }

  persist() {
    if (this.saveTimer) window.clearTimeout(this.saveTimer);
    this.saveTimer = window.setTimeout(() => {
      try {
        // Datei-Inhalte werden nie gespeichert (UploadMeta enthält nur Metadaten)
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
      } catch {
        /* Speicher voll o. ä. – im Demo ignorierbar */
      }
    }, 200);
  }

  /** Mutiert den State über eine Callback-Funktion und benachrichtigt Listener. */
  update(mut: (s: Analyse) => void, notify = true) {
    mut(this.state);
    this.persist();
    if (notify) this.emit();
  }

  /** Setzt einen Wert per Pfad ("stammdaten.vorname"). */
  set(path: string, value: unknown, notify = false) {
    const keys = path.split(".");
    let obj: any = this.state;
    for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]];
    obj[keys[keys.length - 1]] = value;
    this.persist();
    if (notify) this.emit();
  }

  get(path: string): any {
    return path.split(".").reduce((o: any, k) => (o == null ? o : o[k]), this.state);
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  emit() {
    this.listeners.forEach((fn) => fn());
  }

  reset() {
    this.state = createEmptyAnalyse();
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
    this.emit();
  }

  /** Gibt true zurück, wenn der Nutzer bereits sinnvoll Daten erfasst hat. */
  hasMeaningfulData(): boolean {
    const s = this.state.stammdaten;
    return Boolean(s.vorname || s.nachname || s.email || this.state.ziele.ziele.length);
  }
}

export const store = new Store();
